import { createAgentTalkDetector } from './agentTalkDetector';

/**
 * Wraps retell-client-js-sdk behind the common provider contract.
 * `start(config)` expects { accessToken, callId } from the retell-web-call
 * edge function; the SDK itself never sees the API key.
 *
 * The SDK (and its livekit-client dependency, ~1MB) is loaded with a dynamic
 * import on the first start() so it stays out of the main bundle for the Vapi
 * path. stop()/setMuted() no-op until that happens.
 *
 * Transcript snapshots arrive via `subscribeTranscript` (the retell-webhook
 * realtime broadcast) on the v3 gateway transport, and via the SDK's `update`
 * event on LiveKit; both feed the same applyTranscript().
 *
 * The v3 gateway transport doesn't deliver agent_start/stop_talking, so when
 * `detectTalkFromAudio` is on, the speaking indicator is derived from the
 * agent's raw audio level via createAgentTalkDetector() instead.
 */
export const createRetellProvider = ({ subscribeTranscript = null, detectTalkFromAudio = true } = {}) => {
  const listeners = new Map();
  const emit = (event, payload) => {
    (listeners.get(event) || []).forEach((h) => { try { h(payload); } catch (e) { console.error(`[retellProvider] ${event} handler threw`, e); } });
  };

  // Retell transcript snapshots (SDK 'update' events or the webhook broadcast) carry
  // the entire transcript each time; track what we've emitted.
  let lastCount = 0;
  let lastText = '';
  const resetTranscript = () => { lastCount = 0; lastText = ''; };

  const applyTranscript = (t) => {
    if (!Array.isArray(t) || t.length === 0) return;
    if (t.length < lastCount) return; // out-of-order (older) snapshot
    const roleOf = (turn) => (turn?.role === 'agent' ? 'assistant' : 'user');
    const textOf = (turn) => turn?.content ?? '';
    if (t.length > lastCount) {
      // One snapshot can finalise the previous turn and add several new ones.
      if (lastCount > 0) {
        const prev = t[lastCount - 1];
        if (textOf(prev) !== lastText) emit('transcript', { role: roleOf(prev), text: textOf(prev), mode: 'replace' });
      }
      t.slice(lastCount).forEach((turn) => emit('transcript', { role: roleOf(turn), text: textOf(turn), mode: 'append' }));
      lastCount = t.length;
      lastText = textOf(t[t.length - 1]);
      return;
    }
    const last = t[t.length - 1];
    const text = textOf(last);
    if (text !== lastText) {
      lastText = text;
      emit('transcript', { role: roleOf(last), text, mode: 'replace' });
    }
  };

  const onUpdate = (update) => {
    emit('raw-message', update);
    applyTranscript(update?.transcript);
  };

  // v3 gateway calls get their transcript from retell-webhook via Realtime.
  let unsubscribeTranscript = null;
  const closeTranscript = () => { unsubscribeTranscript?.(); unsubscribeTranscript = null; };

  // Set by stop() so the next call_ended is reported as a deliberate hang-up.
  let stopping = false;

  const talk = createAgentTalkDetector();
  const agentStartTalking = () => { emit('speech-end'); emit('speaking-start'); };
  const agentStopTalking = () => { emit('speaking-end'); emit('speech-start'); };
  const onAudio = (samples) => {
    if (!detectTalkFromAudio) return;
    const edge = talk.push(samples);
    if (edge === 'start') agentStartTalking();
    else if (edge === 'stop') agentStopTalking();
  };

  const onCallEnded = () => {
    closeTranscript();
    resetTranscript();
    talk.reset();
    const reason = stopping ? 'customer-ended-call' : 'call-ended';
    stopping = false;
    emit('status-ended', { reason });
    emit('call-end', { reason });
  };

  const bindings = [
    ['call_started', () => emit('call-start')],
    ['call_ready', () => {}],
    ['call_ended', onCallEnded],
    ['agent_start_talking', agentStartTalking],
    ['agent_stop_talking', agentStopTalking],
    ['audio', onAudio],
    ['update', onUpdate],
    ['error', (e) => emit('error', e)],
  ];

  // Memoized lazy client. Handlers are bound exactly once, as the client is created.
  let clientPromise = null;
  let client = null;
  const getClient = () => (clientPromise ??= import('retell-client-js-sdk').then((m) => {
    client = new m.RetellWebClient();
    bindings.forEach(([e, h]) => client.on(e, h));
    return client;
  }));

  return {
    name: 'retell',
    isAvailable: () => true,
    async start(config) {
      if (!config?.accessToken) throw new Error('Retell access token missing');
      // Stop any transport the SDK still holds first, so a synchronous call_ended
      // from it fires before the new subscription exists and cannot close it.
      if (client) client.stopCall();
      resetTranscript();
      talk.reset();
      stopping = false;
      closeTranscript();
      if (subscribeTranscript && config.callId) {
        unsubscribeTranscript = subscribeTranscript(config.callId, applyTranscript);
      }
      // v3 create-web-call may pick a non-LiveKit transport; the SDK defaults to
      // LiveKit unless told otherwise, so forward the connection details as given.
      const { callId, transport, url, iceServers } = config;
      try {
        const c = await getClient();
        await c.startCall({
          accessToken: config.accessToken,
          sampleRate: 24000,
          ...(detectTalkFromAudio ? { emitRawAudioSamples: true } : {}),
          ...(callId ? { callId } : {}),
          ...(transport ? { transport } : {}),
          ...(url ? { url } : {}),
          ...(iceServers ? { iceServers } : {}),
        });
      } catch (e) {
        closeTranscript();
        throw e;
      }
      return { callId: config.callId ?? null, raw: config };
    },
    async stop({ deliberate = true } = {}) {
      closeTranscript();
      if (!clientPromise) return;
      if (deliberate) stopping = true;
      (await clientPromise).stopCall();
    },
    setMuted(muted) {
      if (!clientPromise) return;
      clientPromise.then((client) => { if (muted) client.mute(); else client.unmute(); }).catch(() => {});
    },
    say() { /* Retell web SDK has no server-side say(); intentionally a no-op */ },
    send() { /* no add-message equivalent in the web SDK; no-op */ },
    on(event, handler) { if (!listeners.has(event)) listeners.set(event, new Set()); listeners.get(event).add(handler); },
    off(event, handler) { listeners.get(event)?.delete(handler); },
    destroy() {
      if (client) {
        bindings.forEach(([e, h]) => client.off(e, h));
      }
      closeTranscript();
      listeners.clear();
    },
  };
};
